import { Module, MutationTree, ActionTree, GetterTree } from 'vuex';
import { ADD_TOAST_MESSAGES, RootStore } from '@/features/core/store';
import { remove } from 'lodash';
import {
  GetHeatingControlDevices_spots_items,
  GetHeatingControlDevices_spots_items_metricsLatest_ContinuousMetric,
} from '@/features/app-heating-system/views/control-values/graphql/__generated__/GetHeatingControlDevices';
import { WriteModbusRegister } from '@/features/app-heating-system/views/control-values/graphql/__generated__/WriteModbusRegister';
import { CopyModbusRegister } from '@/features/app-heating-system/views/control-values/graphql/__generated__/CopyModbusRegister';
import { DollarApollo } from 'vue-apollo/types/vue-apollo';
import writeModbusRegister from '@/features/app-heating-system/views/control-values/graphql/saveRegister.gql';
import copyRegister from '@/features/app-heating-system/views/control-values/graphql/copyRegister.gql';
import getRegisterClusterTableQuery from '@/features/app-heating-system/views/tree-node/get-register-cluster-table-data.gql';
import addRegisterClusterTableMutation from '@/features/app-heating-system/views/tree-node/add-register-cluster-table-data.gql';
import { GetCustomerClusterTableConfig } from '@/features/app-heating-system/views/tree-node/__generated__/GetCustomerClusterTableConfig';
import {
  AddRegisterClusterTableData,
  AddRegisterClusterTableDataVariables,
} from '@/features/app-heating-system/views/tree-node/__generated__/AddRegisterClusterTableData';
import { RegisterType } from '@/types/iot-portal';
import { GraphQLError } from 'graphql';

export type Cluster = {
  name: string;
  idx: number;
  registers: Register[];
};

export type Register = {
  registerName: string;
  registerNumber: string;
  metricName: string;
  tableData: string[];
  model: string;
  anlage: string | null;
  value: number | null;
  unit: string | null;
  oldValue: string | null;
};

export type HeatingSystemCluster = {
  cluster: Cluster;
};

export type ClusterDataPayload = {
  model: string;
  clusterIdx: number;
  name: string;
  register: Register;
};

export type RegisterValuesParams = {
  apolloClient: DollarApollo<Vue>;
  customerId: string;
};

export type LoadCustomerClusterTableConfigParams = {
  customerId: string;
  apolloClient: DollarApollo<Vue>;
};

export type HeatingSystemDevice = {
  spotId: string;
  name: string;
  infoAttribute: string;
  metrics: Map<string, number>; //metricName -> metricValue
  deviceAddress: string;
  manufacturer: string;
  model: string;
  version: string;
  spotRole: string;
  clusters: ClusterWithModel[]; // List of clusters with editable registers
};

export type HeatingSystemMetadata = {
  siteId: string;
  name: string;
  devices: HeatingSystemDevice[];
};

export type ClusterWithModel = Cluster & {
  model: string;
};

//export type HeatingSystemStatus = 'offen' | 'in Bearbeitung' | 'bearbeiten' | 'ändern';
export enum HeatingSystemStatus {
  OFFEN = 'offen',
  IN_BEARBEITUNG = 'in bearbeitung',
  BEARBEITEN = 'bearbeiten',
  AENDERN = 'ändern',
}

export type ModbusEditRegistersState = {
  tableData: Register[];
  clusterData: Map<string, Cluster[]>;
  modelNames: string[];
  activeControlModelName: string | null; // Active control model name
  activeHeatingSystemId: string | null; // Active heating system ID
  activeSpotId: string | null; // Active spot ID
  // Heating Control Devices
  heatingControlDevices: GetHeatingControlDevices_spots_items[];
  activeHeatingSystemClusterIdx: number | null; // Active cluster index for the selected heating system
  activeHeatingSystems: HeatingSystemMetadata[]; // List of active heating systems
  canSave: boolean; // Flag to indicate if there are editable registers
  heatingSystemStatuses: Record<string, HeatingSystemStatus>;
};

// Initialize state of the object
const state: ModbusEditRegistersState = {
  // Edit registere modal state
  tableData: [],
  clusterData: new Map<string, Cluster[]>(),
  // Edit Register page state
  activeControlModelName: null,
  activeHeatingSystemId: null,
  activeSpotId: null,
  heatingControlDevices: [] as GetHeatingControlDevices_spots_items[],
  activeHeatingSystemClusterIdx: null,
  modelNames: [],
  activeHeatingSystems: [] as HeatingSystemMetadata[],
  canSave: false,
  heatingSystemStatuses: {},
};

const mutations: MutationTree<ModbusEditRegistersState> = {
  setTabelleData(state, tableData: Register[]) {
    state.tableData = tableData;
  },
  setClusterData(state, clusterData: Map<string, Cluster[]>) {
    state.clusterData = clusterData;
  },
  setCanSave(state, canSave: boolean) {
    state.canSave = canSave;
  },
  addTabelleData(state, tableData: Register) {
    state.tableData.push(tableData);
  },
  setHeatingControlDevices(state, heatingControlDevices: GetHeatingControlDevices_spots_items[]) {
    state.heatingControlDevices = heatingControlDevices;
  },
  setActiveHeatingSystem(state, siteId: string) {
    state.activeHeatingSystemId = siteId;
    // Reset the active cluster index when changing the heating system
    state.activeHeatingSystemClusterIdx = null;
    state.canSave = false;
  },
  setActiveSelectedClusterIdx(state, clusterIdx: number) {
    state.activeHeatingSystemClusterIdx = clusterIdx;
    // Update canSave based on the selected cluster's registers
    state.canSave =
      state.activeHeatingSystems
        .find((system) => system.siteId === state.activeHeatingSystemId)
        ?.devices.find((device) => device.spotId === state.activeSpotId)
        ?.clusters.filter((cluster: ClusterWithModel) => cluster.idx === clusterIdx)
        ?.some((cluster: ClusterWithModel) =>
          cluster.registers?.some((register: Register) => register.value !== null),
        ) || false;
  },
  setActiveControlModel(state, activeControlModelName: string) {
    state.activeControlModelName = activeControlModelName;
    state.canSave = false; // Reset canSave when changing control model
  },
  setActiveHeatingSystems(state, activeHeatingSystems: HeatingSystemMetadata[]) {
    const sortedDeviceHeatingSystems = activeHeatingSystems.map((system) => {
      const sortedDevices = system.devices.sort((a, b) =>
        `${a.name}-${a.spotId}`.localeCompare(`${b.name}-${b.spotId}`),
      );
      return { ...system, devices: sortedDevices };
    });
    state.activeHeatingSystems = sortedDeviceHeatingSystems.sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
  },
  setControlModelNames(state, modelNames: string[]) {
    state.modelNames = modelNames.filter((name, index, self) => {
      return self.indexOf(name) === index; // Ensure unique model names
    });
  },
  setActiveSpotId(state, spotId: string) {
    state.activeSpotId = spotId;
  },
  updateClusterName(state, payload: { model: string; idx: number; name: string }) {
    if (state.clusterData.has(payload.model)) {
      const clusters = state.clusterData.get(payload.model);
      if (clusters) {
        const idx = clusters.findIndex((item) => item.idx === payload.idx);
        if (idx !== -1) {
          const cluster = clusters[idx];
          if (cluster) {
            cluster.name = payload.name;
            clusters.splice(idx, 1, cluster);
            state.clusterData.set(payload.model, clusters);
          }
        }
      }
    }
  },
  deleteCluster(state, payload: { model: string; clusterIdx: number }) {
    if (state.clusterData.has(payload.model)) {
      const clusters = state.clusterData.get(payload.model);
      if (clusters) {
        const clusterIdx = clusters.findIndex((item) => item.idx === payload.clusterIdx);
        if (clusterIdx !== -1) {
          clusters.splice(clusterIdx, 1);
          state.clusterData.set(payload.model, clusters);
        }
      }
    }
  },
  addClusterData(state, clusterData: ClusterDataPayload) {
    if (!state.clusterData.has(clusterData.model)) {
      state.clusterData.set(clusterData.model, []);
    }

    const clusters = state.clusterData.get(clusterData.model);
    if (clusters?.length === 0 || clusters?.find((item) => item.idx === clusterData.clusterIdx) === undefined) {
      clusters?.push({
        name: clusterData.name,
        idx: clusterData.clusterIdx,
        registers: clusterData.register?.metricName ? [clusterData.register] : [],
      });
    } else {
      const clusterIdx = clusters.findIndex((item) => item.idx === clusterData.clusterIdx);
      if (clusterIdx !== -1) {
        const cluster = clusters[clusterIdx];
        cluster.name = clusterData.name;
        if (clusterData.register?.registerName) {
          const registerIdx = cluster.registers.findIndex(
            (item) => item.registerName === clusterData.register.registerName,
          );
          if (registerIdx === -1) {
            cluster.registers.push(clusterData.register);
          } else {
            cluster.registers.splice(registerIdx, 1, clusterData.register);
          }
        }
        clusters.splice(clusterIdx, 1, cluster);
        state.clusterData.set(clusterData.model, clusters);
      }
    }
  },
  removeTabelleData(state, tableData: Register) {
    state.tableData = remove(
      state.tableData,
      (item) =>
        !(item.registerName.includes(tableData.registerName) && item.registerNumber.includes(tableData.registerNumber)),
    );
  },
  removeClusterData(state: ModbusEditRegistersState, payload: ClusterDataPayload) {
    if (state.clusterData.has(payload.model)) {
      const clusters = state.clusterData.get(payload.model);
      if (clusters) {
        const idx = clusters.findIndex((item) => item.idx === payload.clusterIdx);
        if (idx !== -1) {
          // If a register is specified, remove it from the cluster
          if (payload.register) {
            clusters[idx].registers = clusters[idx].registers.filter(
              (item) =>
                !(
                  item.registerName === payload.register.registerName &&
                  item.model === payload.register.model &&
                  item.registerNumber === payload.register.registerNumber
                ),
            );
          }
          // Do NOT remove the cluster if it becomes empty here!
          state.clusterData.set(payload.model, [...clusters]);
        }
      }
    }
  },
  setHeatingSystemStatus(state, { siteId, status }: { siteId: string; status: HeatingSystemStatus }) {
    state.heatingSystemStatuses = {
      ...state.heatingSystemStatuses,
      [siteId]: status,
    };
  },
  resetHeatingSystemStatuses(state) {
    state.heatingSystemStatuses = {};
  },
  resetActiveHeatingSystem(state) {
    state.activeHeatingSystemId = null;
    state.activeControlModelName = null;
    state.activeHeatingSystemClusterIdx = null;
  },
};

const getters: GetterTree<ModbusEditRegistersState, RootStore> = {
  tableData(state) {
    return state.tableData;
  },
  clusterData(state) {
    return state.clusterData;
  },
  canSaveRegisters(state) {
    return state.canSave;
  },
  // Used in the cluster component
  activeHeatingSystemClusters(state) {
    if (state.activeHeatingSystemId && state.activeSpotId) {
      return (
        state.activeHeatingSystems
          .find((system) => system.siteId === state.activeHeatingSystemId)
          ?.devices.find((spot) => spot.spotId === state.activeSpotId)?.clusters || []
      );
    }
    return [];
  },
  activeHeatingSystemClusterIdx(state) {
    return state.activeHeatingSystemClusterIdx;
  },
  activeHeatingSystemClusterRegisters(state) {
    if (state.activeHeatingSystemId && state.activeSpotId && state.activeHeatingSystemClusterIdx !== null) {
      return (
        state.activeHeatingSystems
          .find((system) => system.siteId === state.activeHeatingSystemId)
          ?.devices.find((spot) => spot.spotId === state.activeSpotId)
          ?.clusters.find((cluster) => cluster.idx === state.activeHeatingSystemClusterIdx)?.registers || null
      );
    }
    return null;
  },
  activeHeatingSystemMetrics(state) {
    if (state.activeHeatingSystemId && state.activeSpotId) {
      const activeHeatingSystem = state.activeHeatingSystems.find(
        (system) => system.siteId === state.activeHeatingSystemId,
      );
      if (activeHeatingSystem) {
        const activeDevice = activeHeatingSystem.devices.find((spot) => spot.spotId === state.activeSpotId);
        if (!activeDevice) return null;
        return Array.from(activeDevice.metrics)?.reduce((acc, [metricName, value]) => {
          acc[metricName] = value;
          return acc;
        }, {} as Record<string, number>);
      }
      return null;
    }
  },
  activeControlModelName(state) {
    return state.activeControlModelName;
  },
  activeHeatingSystemId(state) {
    return state.activeHeatingSystemId;
  },
  activeHeatingSystems(state) {
    return state.activeHeatingSystems;
  },
  activeHeatingSystemDevices(state) {
    return state.activeHeatingSystems.find((system) => system.siteId === state.activeHeatingSystemId)?.devices || [];
  },
  filterHeatingSystemNames: (state) => {
    return (
      state.heatingControlDevices
        .filter(
          (device: GetHeatingControlDevices_spots_items) =>
            device.activeDeviceMount?.modbusModel === state.activeControlModelName,
        )
        .filter(
          (device: GetHeatingControlDevices_spots_items) => device.property?.node?.id !== state.activeHeatingSystemId,
        )
        .map((device: GetHeatingControlDevices_spots_items) => ({
          name: device.property?.node?.name,
          id: device.property?.node?.id,
        }))
        // Get Unique values based on site name and id
        .filter((value, index, self) => index === self.findIndex((t) => t.id === value.id))
    );
  },
  filteredControlModelNames: (state) => {
    return state.modelNames.filter((value, index, self) => index === self.findIndex((t) => t === value));
  },
};

const actions: ActionTree<ModbusEditRegistersState, RootStore> = {
  removeRegisterFromCluster({ commit }, payload: ClusterDataPayload) {
    commit('removeClusterData', payload);
  },
  async saveClusterConfigToLocalStorage({ state, dispatch }, params: LoadCustomerClusterTableConfigParams) {
    const clusterData = Array.from(state.clusterData.entries()).map(([model, clusters]) => ({
      model,
      clusters,
    }));
    localStorage.setItem(`stateClusterData_${params.customerId}`, JSON.stringify(clusterData));
    try {
      const response = await params.apolloClient.mutate<
        AddRegisterClusterTableData,
        AddRegisterClusterTableDataVariables
      >({
        mutation: addRegisterClusterTableMutation,
        variables: {
          input: {
            customerId: params.customerId,
            registerType: RegisterType.EDIT,
            data: clusterData,
          },
        },
      });

      if (response.errors) {
        // Handle errors
        await dispatch(ADD_TOAST_MESSAGES, {
          messages: [{ text: 'Unable to save cluster config. Will retry later.' }],
        });
      }
    } catch (error) {
      await dispatch(ADD_TOAST_MESSAGES, {
        messages: [{ text: 'Unable to load cluster config from backend. Please try again later.' }],
      });
    }
  },
  async saveTableDataToLocalStorage({ state, dispatch }, params: LoadCustomerClusterTableConfigParams) {
    const tableData = state.tableData.map((item) => ({
      registerName: item.registerName,
      registerNumber: item.registerNumber,
      model: item.model,
      value: item.value,
    }));
    localStorage.setItem(`stateTableData_${params.customerId}`, JSON.stringify(tableData));
    try {
      const response = await params.apolloClient.mutate<
        AddRegisterClusterTableData,
        AddRegisterClusterTableDataVariables
      >({
        mutation: addRegisterClusterTableMutation,
        variables: {
          input: {
            customerId: params.customerId,
            registerType: RegisterType.TABLE,
            data: tableData,
          },
        },
      });

      if (response.errors) {
        // Handle errors
        await dispatch(ADD_TOAST_MESSAGES, {
          messages: [{ text: 'Unable to save cluster config. Will retry later.' }],
        });
      }
    } catch (error) {
      await dispatch(ADD_TOAST_MESSAGES, {
        messages: [{ text: 'Unable to load cluster config from backend. Please try again later.' }],
      });
    }
  },
  async loadClusterTableConfig({ commit, dispatch }, params: LoadCustomerClusterTableConfigParams) {
    try {
      const response = await params.apolloClient.query<GetCustomerClusterTableConfig>({
        query: getRegisterClusterTableQuery,
        variables: { input: params.customerId },
        fetchPolicy: 'no-cache',
      });
      const edit = response.data?.registersClusterTableData.edit;
      const table = response.data?.registersClusterTableData.table;

      const clusterMap = new Map(edit.map((item) => [item.model, item.clusters]));
      const firstModelName = edit[0]?.model || null;
      commit('setClusterData', clusterMap);
      commit('setActiveControlModel', firstModelName);

      const registerList = table.map((item) => ({
        ...item,
      }));
      commit('setTabelleData', registerList);
    } catch (error) {
      await dispatch(ADD_TOAST_MESSAGES, {
        messages: [{ text: 'Unable to load cluster config from backend. Please try again later.' }],
      });
    }
  },
  async setActiveHeatingSystem({ commit, state, dispatch }, siteId: string) {
    commit('setActiveHeatingSystem', siteId);

    // Get available models for this HS
    const devices = state.heatingControlDevices.filter(
      (device: GetHeatingControlDevices_spots_items) =>
        device.property?.node?.id === siteId && device.activeDeviceMount?.modbusModel,
    );
    const models = Array.from(new Set(devices.map((device) => device?.activeDeviceMount?.modbusModel)));
    commit('setControlModelNames', models);

    // Auto-select the first model if available
    if (models.length > 0) {
      await dispatch('setActiveControlModel', devices.sort((a, b) => a.name.localeCompare(b.name)).at(0)?.id || '');
    }
  },
  resetStatuses({ commit }) {
    commit('resetHeatingSystemStatuses');
  },
  async setHeatingControlDevicesAction({ commit }, heatingControlDevices: GetHeatingControlDevices_spots_items[]) {
    commit('setHeatingControlDevices', heatingControlDevices);
    const modelNamesSet = new Set(state.modelNames);
    const activeHeatingSystems = heatingControlDevices
      .filter(
        (device: GetHeatingControlDevices_spots_items) =>
          device.activeDeviceMount?.modbusModel && modelNamesSet.has(device.activeDeviceMount.modbusModel),
      )
      .reduce((acc, device: GetHeatingControlDevices_spots_items) => {
        const siteId = device.property?.node?.id;
        const siteName = device.property?.node?.name;
        if (!siteId || !siteName) return acc;
        const key = `${siteId}/${siteName}`;
        const newDevice: HeatingSystemDevice = {
          spotId: device.id,
          name: device.name,
          infoAttribute: device.activeDeviceMount?.attributes?.find((attr) => attr.name === 'info')?.value || '',
          deviceAddress: device.activeDeviceMount?.deviceAddress || '',
          manufacturer: device.activeDeviceMount?.manufacturer || '',
          model: device.activeDeviceMount?.modbusModel || '',
          version: device.activeDeviceMount?.modbusVersion || '',
          spotRole: device.role,
          metrics: new Map(
            device.metricsLatest
              .filter(
                (metric): metric is GetHeatingControlDevices_spots_items_metricsLatest_ContinuousMetric =>
                  metric?.__typename === 'ContinuousMetric',
              )
              .map((metric: GetHeatingControlDevices_spots_items_metricsLatest_ContinuousMetric) => [
                metric.latest.name,
                metric.latest.value,
              ]),
          ),
          clusters: [],
        };
        acc.set(key, (acc.get(key) || []).concat(newDevice));
        return acc;
      }, new Map<string, HeatingSystemDevice[]>());
    const activeHeatingSystemsList: HeatingSystemMetadata[] = Array.from(activeHeatingSystems.entries()).map(
      ([key, devices]) => {
        const [siteId, siteName] = key.split('/');

        return {
          name: siteName,
          siteId,
          devices,
        };
      },
    );

    commit('setActiveHeatingSystems', activeHeatingSystemsList);
  },
  async setActiveControlModel({ commit, state }, spotId: string) {
    if (!state.activeHeatingSystemId) {
      return;
    }
    // Set the spot ID as the active spot
    commit('setActiveSpotId', spotId);

    const activeHeatingSystem = state.activeHeatingSystems.find(
      (system) => system.siteId === state.activeHeatingSystemId,
    );
    if (!activeHeatingSystem) return;
    const activeDevice = activeHeatingSystem.devices.find((spot) => spot.spotId === state.activeSpotId);
    if (!activeDevice) return;
    const activeControlModelName = activeDevice.model;

    const siteId = state.activeHeatingSystemId;
    if (!siteId) return;

    commit('setActiveControlModel', activeControlModelName);

    const clusters = state.clusterData.get(activeControlModelName);
    const existingDeviceClusters = activeDevice.clusters.filter((cluster) => cluster.model === activeControlModelName);

    if (existingDeviceClusters.length === 0 && clusters) {
      activeDevice.clusters = clusters.map((cluster) => ({
        ...cluster,
        model: activeControlModelName,
      })) as ClusterWithModel[];
      // Update the active heating systems in the state to ensure reactivity
      activeHeatingSystem.devices = [
        ...activeHeatingSystem.devices.filter((spot) => spot.spotId !== state.activeSpotId),
        activeDevice,
      ];
      commit('setActiveHeatingSystems', [
        ...state.activeHeatingSystems.filter((system) => system.siteId !== state.activeHeatingSystemId),
        activeHeatingSystem,
      ]);
    } else if (clusters) {
      // Handle cases where a user adds a new cluster or updates an existing one
      const newClusters = clusters
        .filter((c) => !existingDeviceClusters.some((ec) => ec.idx === c.idx))
        .map((cluster) => ({
          ...cluster,
          model: activeControlModelName,
        })) as ClusterWithModel[];

      // If clusters already exist, ensure that we also update the existing clusters with the new registers
      const updatedClusters = existingDeviceClusters
        .filter((c) => clusters.some((cluster) => c.idx === cluster.idx))
        .map((cluster) => {
          const newClusterInfo = clusters.find((c) => c.idx === cluster.idx);
          cluster.name = newClusterInfo?.name || cluster.name;
          if (newClusterInfo) {
            const existingRegisterNumbers = new Set(cluster.registers.map((r) => r.registerNumber));
            const newRegisters = newClusterInfo.registers.filter((r) => !existingRegisterNumbers.has(r.registerNumber));
            cluster.registers = [
              ...cluster.registers.filter((r) =>
                newClusterInfo.registers.some((nr) => nr.registerNumber === r.registerNumber),
              ),
              ...newRegisters,
            ];
          }
          return { ...cluster, model: activeControlModelName };
        });
      activeDevice.clusters = [...updatedClusters, ...newClusters].sort((a, b) =>
        `${a.name}-${a.idx}`.localeCompare(`${b.name}-${b.idx}`),
      );
      // Update the active heating systems in the state to ensure reactivity
      activeHeatingSystem.devices = [
        ...activeHeatingSystem.devices.filter((spot) => spot.spotId !== state.activeSpotId),
        activeDevice,
      ];
      commit('setActiveHeatingSystems', [
        ...state.activeHeatingSystems.filter((system) => system.siteId !== state.activeHeatingSystemId),
        activeHeatingSystem,
      ]);
    }
    commit(
      'setCanSave',
      activeDevice.clusters
        .find((cluster) => cluster.idx === state.activeHeatingSystemClusterIdx)
        ?.registers.some((reg) => reg.value !== null) || false,
    );
  },
  async saveRegisterValue({ state, commit }, register: Register): Promise<void> {
    const activeHeatingSystem = state.activeHeatingSystems.find(
      (system) => system.siteId === state.activeHeatingSystemId,
    );
    if (!activeHeatingSystem || !state.activeHeatingSystemId || !state.activeSpotId) return;
    const activeDevice = activeHeatingSystem.devices.find((spot) => spot.spotId === state.activeSpotId);
    if (!activeDevice) return;
    if (state.activeHeatingSystemId) {
      const clusters = activeDevice.clusters;
      if (clusters && state.activeHeatingSystemClusterIdx !== null) {
        const editedClusterIndex = clusters.findIndex((cluster) => cluster.idx === state.activeHeatingSystemClusterIdx);
        if (editedClusterIndex !== -1) {
          // Update the register value in the edited cluster
          clusters[editedClusterIndex].registers = clusters[editedClusterIndex].registers.map((reg) =>
            reg.registerName === register.registerName && reg.anlage === register.anlage
              ? { ...reg, value: register.value, oldValue: register.oldValue }
              : reg,
          );
          activeDevice.clusters = clusters.sort((a, b) => `${a.name}-${a.idx}`.localeCompare(`${b.name}-${b.idx}`));
          // Update the active heating systems in the state to ensure reactivity
          activeHeatingSystem.devices = [
            ...activeHeatingSystem.devices.filter((spot) => spot.spotId !== state.activeSpotId),
            activeDevice,
          ];
          commit('setActiveHeatingSystems', [
            ...state.activeHeatingSystems.filter((system) => system.siteId !== state.activeHeatingSystemId),
            activeHeatingSystem,
          ]);
          commit(
            'setCanSave',
            clusters[editedClusterIndex].registers.some((reg) => reg.value !== null),
          ); // Update canSave based on register value
          const cluster = clusters[editedClusterIndex];
          const changedCount = cluster.registers.filter((reg) => reg.value !== null).length;
          const totalCount = cluster.registers.length;

          commit('setHeatingSystemStatus', {
            siteId: state.activeHeatingSystemId,
            status: changedCount === 0 ? 'offen' : changedCount === totalCount ? 'ändern' : 'bearbeiten',
          });
        }
      }
    }
  },
  async saveHSRegisterValues({ state, dispatch, commit }, params: RegisterValuesParams): Promise<void> {
    try {
      // Get the active control model to get the device serial number
      if (!state.canSave) {
        throw new Error('No active control model selected');
      }

      // Get all devices matching the active model
      // const matchingDevices = state.devices.filter((device) => device.deviceModel === state.activeControlModelName);
      const activeDevice = state.activeHeatingSystems
        .find((system) => system.siteId === state.activeHeatingSystemId)
        ?.devices.find((spot) => spot.spotId === state.activeSpotId);
      if (activeDevice) {
        const editableRegisters: Register[] = (await dispatch('collectRegisterValue')) as Register[];

        const payload = [
          {
            debug: false,
            customerId: params.customerId,
            deviceAddress: activeDevice?.deviceAddress,
            deviceRole: activeDevice?.spotRole,
            manufacturer: activeDevice?.manufacturer,
            model: activeDevice?.model,
            version: activeDevice?.version,
            registers: editableRegisters.map((r) => ({
              registerName: r.registerName,
              value: Number(r.value),
              oldValue: Number(r.oldValue),
            })),
          },
        ];

        try {
          commit('setCanSave', false); // Set canSave to false while saving
          const response = await params.apolloClient.mutate<WriteModbusRegister>({
            mutation: writeModbusRegister,
            variables: {
              input: payload,
            },
          });
          await dispatch('handleResponseError', response.errors);
          const result = response.data?.editModbusRegister ?? [];
          for (const res of result) {
            if (res) {
              await dispatch(ADD_TOAST_MESSAGES, {
                messages: [
                  {
                    text: res.numberOfMessages != null && `Successfully wrote ${res.numberOfMessages}`,
                  },
                ],
              });
            }
          }
        } catch (error) {
          await dispatch('handleError', error);
        } finally {
          // Reset the canSave flag after saving
          commit('setCanSave', true);
        }
      }
    } catch (error) {
      console.error('Failed to save register values:', error);
      throw error;
    }
  },
  async collectRegisterValue(): Promise<Register[]> {
    // Get registers that have a value set
    const editableRegisters: Register[] = [];
    if (state.activeHeatingSystemId && state.activeSpotId) {
      const clusters = state.activeHeatingSystems
        .find((system) => system.siteId === state.activeHeatingSystemId)
        ?.devices.find((device) => device.spotId === state.activeSpotId)
        ?.clusters.filter((cluster: ClusterWithModel) => cluster.idx === state.activeHeatingSystemClusterIdx);
      if (clusters && state.activeHeatingSystemClusterIdx !== null) {
        clusters.forEach((cluster) => {
          editableRegisters.push(...(cluster?.registers.filter((register) => register.value !== null) || []));
        });
      }
    }
    return editableRegisters;
  },
  async handleError({ dispatch }, error: unknown) {
    const message = error instanceof Error ? error.message : String(error ?? '');
    await dispatch(ADD_TOAST_MESSAGES, {
      messages: [{ text: message }],
    });
  },
  async handleResponseError({ dispatch }, errors?: ReadonlyArray<GraphQLError>) {
    if (errors && errors.length > 0) {
      const message = errors.map((e: GraphQLError) => e.message).join(', ');
      await dispatch(ADD_TOAST_MESSAGES, {
        messages: [{ text: message }],
      });
    }
  },
  // TODO -- fix this
  async copyHSRegisterValues(
    { state, dispatch },
    params: RegisterValuesParams & { selectedHeatingSystems: string[] },
  ): Promise<void> {
    try {
      // Get the active control model to get the device serial number
      if (!state.canSave) {
        throw new Error('No active control model selected');
      }

      // Use the selected heating system IDs
      const { selectedHeatingSystems } = params;
      if (!selectedHeatingSystems || selectedHeatingSystems.length === 0) {
        throw new Error('No heating systems selected');
      }

      // Filter only matching devices from selected systems
      const matchingDevices = state.heatingControlDevices
        .filter((device: GetHeatingControlDevices_spots_items) =>
          selectedHeatingSystems.includes(device.property?.node?.id ?? ''),
        )
        .filter(
          (device: GetHeatingControlDevices_spots_items) =>
            device.activeDeviceMount?.modbusModel === state.activeControlModelName,
        );
      const editableRegisters: Register[] = (await dispatch('collectRegisterValue')) as Register[];

      if (matchingDevices.length === 0 || editableRegisters.length === 0) {
        throw new Error('No matching devices found for selected model');
      }
      // build inputs for all devices
      const inputs = matchingDevices
        .filter((device) => {
          const m = device.activeDeviceMount;
          return m?.deviceAddress && m.manufacturer && m.modbusModel && m.modbusVersion;
        })
        .map((device) => {
          const m = device.activeDeviceMount!; // safe after filter
          return {
            debug: false,
            deviceAddress: m.deviceAddress,
            deviceRole: device.role,
            manufacturer: m.manufacturer,
            model: m.modbusModel,
            version: m.modbusVersion,
            registers: editableRegisters.map((r) => ({
              registerName: r.registerName,
              value: Number(r.value),
            })),
          };
        });

      try {
        // call mutation once with all inputs
        const response = await params.apolloClient.mutate<CopyModbusRegister>({
          mutation: copyRegister,
          variables: { input: inputs },
        });
        await dispatch('handleResponseError', response.errors);
        const results = response.data?.editModbusRegister ?? [];
        for (const result of results) {
          if (result) {
            await dispatch(ADD_TOAST_MESSAGES, {
              messages: [
                {
                  text:
                    result.numberOfMessages != null
                      ? `Successfully wrote ${result.numberOfMessages} registers to ${result.deviceAddress}.`
                      : `Successfully wrote registers to ${result.deviceAddress}.`,
                },
              ],
            });
          }
        }
      } catch (error) {
        await dispatch('handleError', error);
      }
    } catch (error) {
      console.error('Failed to copy register values:', error);
      throw error;
    }
  },
};

// Export the module
export const ModbusEditRegistersModule: Module<ModbusEditRegistersState, any> = {
  state,
  mutations,
  actions,
  getters,
};
